import { useState } from 'react';
import {
    Box, Typography, TextField, IconButton, Button, Stack, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const BLUE = '#004df2';
const EMPTY_DATASET = { name: '', description: '' };

// "Data we pull" for a provider — the canonical, human-typed list of typical
// segments/datasets and what's in them (Students, Attendance, Incidents,
// etc). Deliberately a structured repeatable list, not one big free-text
// blob, so adding one more dataset is just another row, not re-editing a
// wall of text. Shared across every client that has this provider — same
// scope as Category/What-it-is on ProviderInfoCard, just its own field.

function DatasetForm({ form, setForm, onSave, onCancel }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <TextField
                size="small" label="Dataset" placeholder="e.g. Students, Attendance, Incidents"
                value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
                size="small" label="What's in it" multiline minRows={2} maxRows={12}
                placeholder="What this dataset contains"
                value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button size="small" variant="contained" onClick={onSave} disabled={!form.name?.trim()}
                    sx={{ textTransform: 'none', backgroundColor: BLUE, borderRadius: 2, '&:hover': { backgroundColor: '#0040c8' } }}>
                    Save
                </Button>
            </Box>
        </Box>
    );
}

const DatasetsList = ({ datasets, onChange }) => {
    const [editing, setEditing] = useState(null); // 'new' | index | null
    const [form, setForm] = useState(EMPTY_DATASET);

    const list = datasets || [];

    const save = () => {
        if (!form.name?.trim()) return;
        onChange(editing === 'new' ? [...list, form] : list.map((d, i) => (i === editing ? form : d)));
        setEditing(null);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3, flex: 1 }}>
                    DATA WE PULL
                </Typography>
                {editing === null && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setForm(EMPTY_DATASET); setEditing('new'); }}
                        sx={{ textTransform: 'none', color: BLUE, fontSize: 11.5, minWidth: 0, py: 0.25 }}>
                        Add dataset
                    </Button>
                )}
            </Box>

            {list.map((d, i) => (
                <Box key={`${d.name}-${i}`}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
                        <Box sx={{ minWidth: 140, maxWidth: 180, flexShrink: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{d.name}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flex: 1, whiteSpace: 'pre-wrap' }}>
                            {d.description || '—'}
                        </Typography>
                        <IconButton size="small" onClick={() => { setForm(d); setEditing(i); }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => onChange(list.filter((_, x) => x !== i))}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Stack>
                    {i < list.length - 1 && <Divider />}
                </Box>
            ))}

            {editing !== null && (
                <DatasetForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} />
            )}

            {list.length === 0 && editing === null && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', py: 1 }}>
                    No datasets documented yet — add the first one above.
                </Typography>
            )}
        </Box>
    );
};

export default DatasetsList;
